import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, KeyValue, detailActionClass } from '@/components/data/DetailPane'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { StatusText } from '@/components/data/StatusText'
import { useSelection } from '@/lib/useSelection'
import { plural } from '@/lib/utils'

import { SectionError, SectionStrip, TOKEN_HINT } from './SectionLabel'
import type { DialogState } from './types'

export type ProjectRule = {
  rule_uuid: string
  name: string | null
  state: string | null
  scope: string | null
}
export type ProjectAutomation = {
  project_rules: ProjectRule[]
  global_count: number
  warnings: string[]
}

const ruleState = (r: ProjectRule) => (r.state === 'ENABLED' ? 'enabled' : (r.state ?? 'unknown').toLowerCase())

const COLUMNS: Column<ProjectRule>[] = [
  { key: 'name', header: 'Rule', render: (r) => r.name ?? r.rule_uuid, title: (r) => r.name ?? r.rule_uuid },
  { key: 'scope', header: 'Scope', mono: true, width: '18%', render: (r) => r.scope ?? '—' },
  { key: 'state', header: 'State', width: '110px', render: (r) => <StatusText status={ruleState(r)} />, title: ruleState },
]

export function AutomationSection({ automation, err, refresh, onDialog }: {
  automation: ProjectAutomation | null
  err: boolean
  refresh: () => void
  onDialog: (d: DialogState) => void
}) {
  const [sel, setSel] = useSelection()
  const selected = automation?.project_rules.find((r) => r.rule_uuid === sel) ?? null
  const on = selected?.state === 'ENABLED'
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionStrip label="automation" onRefresh={refresh}>
        {automation && automation.global_count > 0 && (
          <span className="font-mono text-[11px] text-text-3">
            {plural(automation.global_count, 'global rule')} also apply org-wide
          </span>
        )}
        {automation && automation.warnings.length > 0 && (
          <span className="text-[12px] text-warn">{automation.warnings.join(' · ')}</span>
        )}
      </SectionStrip>
      {err ? <SectionError>Couldn’t load automation — {TOKEN_HINT}</SectionError>
        : automation === null ? <LoadingRows label="Loading automation rules" rows={4} />
        : (
          <SplitView
            onCloseDetail={() => setSel(null)}
            detail={selected && (
              <DetailPane
                eyebrow="Automation rule"
                title={selected.name ?? selected.rule_uuid}
                status={<StatusText status={ruleState(selected)} />}
                actions={(
                  <button
                    type="button"
                    className={detailActionClass}
                    onClick={() => onDialog({
                      op: 'automation.set_rule_state',
                      params: { rule_uuids: [selected.rule_uuid], enabled: !on },
                      title: `${on ? 'Disable' : 'Enable'} "${selected.name ?? selected.rule_uuid}"?`,
                      onDone: refresh,
                    })}
                  >
                    {on ? 'Disable rule' : 'Enable rule'}
                  </button>
                )}
              >
                <KeyValue
                  items={[
                    ['Scope', <span className="font-mono">{selected.scope ?? '—'}</span>],
                    ['Rule id', <span className="font-mono text-[12px] text-text-2">{selected.rule_uuid}</span>],
                  ]}
                />
              </DetailPane>
            )}
            list={(
              <DataTable label="Project automation rules" rows={automation.project_rules} columns={COLUMNS}
                rowId={(r) => r.rule_uuid} selectedId={sel} onSelect={setSel}
                empty="No project-scoped automation rules." />
            )}
          />
        )}
    </div>
  )
}
